import {
  RULES,
  chooseAiPlan,
  cloneState,
  createOpenSupplyGame,
  getCard,
  getDevotion,
  getDevotionTargets,
  getCardTargets,
  getReplayableCard,
  getServant,
  getStage,
  legalFocusLanes,
  resolveHour,
  validatePlan,
  type CardDefinition,
  type Depth,
  type DevotionDefinition,
  type GameState,
  type HourResolution,
  type Lane,
  type PlayerIndex,
  type PlayerPlan,
} from "../../shared/index.js";
import type {
  BattlePhase,
  BattleSession,
  BattleUiState,
} from "./BattleSession.js";

export interface SoloBattleOptions {
  stageId: string;
  playerServantId: string;
  opponentServantId: string;
  openingObjectCardId: string | "random" | null;
  seed: number;
}

export const DEFAULT_SOLO_BATTLE_OPTIONS: SoloBattleOptions = Object.freeze({
  stageId: "looping-street",
  playerServantId: "hourglass",
  opponentServantId: "flint",
  openingObjectCardId: "random",
  seed: 20260713,
});

export class SoloBattleSession implements BattleSession {
  public readonly mode = "solo" as const;
  public readonly viewerIndex = 0 as const;
  public state: GameState;
  public draft: PlayerPlan;
  public phase: BattlePhase = "planning";
  public error = "";

  private presentationState: GameState | null = null;

  public constructor(public readonly options: SoloBattleOptions = DEFAULT_SOLO_BATTLE_OPTIONS) {
    this.state = this.createState();
    this.draft = this.createDraft();
  }

  public get startLog(): string {
    const stage = getStage(this.state.stageId);
    const opening = this.state.openSupply?.openingObjectCardId
      ? getCard(this.state.openSupply.openingObjectCardId)
      : null;
    const openingText = opening
      ? "中央には《" + opening.name + "》が最初から浮かんでいる。"
      : "《窓》にはまだ何も浮かんでいない。";
    return "自由対戦《" + (stage?.name ?? "静かな窓") + "》を開始。" + openingText;
  }

  public reset(): void {
    this.state = this.createState();
    this.phase = "planning";
    this.error = "";
    this.presentationState = null;
    this.draft = this.createDraft();
  }

  public get selectedCard(): CardDefinition | null {
    return this.draft.supplyCardId ? getCard(this.draft.supplyCardId) : null;
  }

  public get selectedDevotion(): DevotionDefinition | null {
    return this.draft.devotionId ? getDevotion(this.draft.devotionId) : null;
  }

  public selectCard(cardId: string): void {
    if (this.phase !== "planning" || !this.isSupplyCardSelectable(cardId)) return;
    this.error = "";
    if (this.draft.supplyCardId === cardId) {
      delete this.draft.supplyCardId;
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    this.draft.supplyCardId = cardId;
    this.draft.devotionId = "";
    this.draft.target = {};
  }

  public selectDevotion(): void {
    if (this.phase !== "planning") return;
    const player = this.state.players[0];
    const servant = getServant(player.servantId);
    if (!servant || player.devotionUsed || getDevotionTargets(this.state, 0).length === 0) return;
    this.error = "";
    if (this.draft.devotionId === servant.devotion.id) {
      this.draft.devotionId = "";
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    delete this.draft.supplyCardId;
    this.draft.devotionId = servant.devotion.id;
    this.draft.target = {};
  }

  public selectDevotionTarget(playerIndex: PlayerIndex): void {
    if (this.phase !== "planning" || this.selectedDevotion?.targetType !== "player") return;
    this.error = "";
    this.draft.target = { playerIndex };
  }

  public selectFocus(lane: Lane): void {
    if (this.phase !== "planning") return;
    const legal = legalFocusLanes(this.state, 0);
    if (!legal.includes(lane)) {
      this.error = "現在の注目からは、隣のレーンまでしか移れません。";
      return;
    }
    this.error = "";
    this.draft.focusLane = lane;
  }

  public selectBoardPosition(lane: Lane, depth: Depth, objectId?: string): void {
    if (this.phase !== "planning") return;
    const devotion = this.selectedDevotion;
    const card = this.selectedCard
      ?? (devotion?.targetType === "replay" ? getReplayableCard(this.state, 0) : null);
    this.error = "";

    if (devotion?.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }
    if (card?.kind === "object") {
      this.draft.target = { lane };
      return;
    }
    if (!card || card.kind !== "spell") {
      this.selectFocus(lane);
      return;
    }
    if (card.targetType === "none") {
      this.draft.target = {};
      return;
    }
    if (card.targetType === "lane") {
      this.draft.target = { lane };
      return;
    }
    if (card.targetType === "depth") {
      this.draft.target = { depth };
      return;
    }
    if (card.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }

    const source = this.state.board.find((object) => object.id === this.draft.target.objectId);
    if (source) {
      if (card.targetType === "object-direction") {
        const direction = lane - source.lane;
        if (depth === source.depth && (direction === -1 || direction === 1)) {
          this.draft.target = { objectId: source.id, direction };
          return;
        }
      }
      if (card.targetType === "object-depth"
        && lane === source.lane
        && Math.abs(depth - source.depth) === 1) {
        this.draft.target = { objectId: source.id, depth };
        return;
      }
      if (objectId === source.id) return;
    }
    if (objectId) this.draft.target = { objectId };
  }

  public prepareResolution(isPass: boolean): HourResolution | null {
    if (this.phase !== "planning" || this.state.result) return null;
    const proposed = isPass ? this.passPlan() : clonePlan(this.draft);
    const validation = validatePlan(this.state, 0, proposed);
    if (!validation.ok) {
      this.error = validation.reason;
      return null;
    }

    this.error = "";
    this.presentationState = cloneState(this.state);
    this.phase = "resolving";
    const opponentPlan = chooseAiPlan(this.state, 1);
    return resolveHour(this.state, [proposed, opponentPlan]);
  }

  public finishResolution(): void {
    this.presentationState = null;
    if (this.state.result) {
      this.phase = "result";
      return;
    }
    this.phase = "planning";
    this.draft = this.createDraft();
  }

  public getUiState(): BattleUiState {
    const displayState = this.presentationState ?? this.state;
    const actionSelected = Boolean(this.draft.supplyCardId || this.draft.devotionId);
    const actionValidation = validatePlan(this.state, 0, this.draft);
    const passValidation = validatePlan(this.state, 0, this.passPlan());
    const servant = getServant(this.state.players[0].servantId);
    const supply = this.state.openSupply;
    const displaySupply = displayState.openSupply;
    return {
      mode: this.mode,
      viewerIndex: this.viewerIndex,
      cardSource: "supply",
      state: cloneState(displayState),
      draft: clonePlan(this.draft),
      phase: this.phase,
      legalFocusLanes: legalFocusLanes(this.state, 0),
      allowedCardInstanceIds: [],
      supplyCards: (supply?.cards ?? []).map((entry) => ({
        cardId: entry.cardId,
        cost: entry.cost,
        limited: entry.limited,
        available: this.phase === "planning" && this.isSupplyCardSelectable(entry.cardId),
        opponentAvailable: entry.availableTo[1],
      })),
      energy: displaySupply ? [...displaySupply.energy] as [number, number] : null,
      maxEnergy: supply?.maxEnergy ?? RULES.openSupplyMaxEnergy,
      devotionAvailable: this.phase === "planning"
        && !this.state.players[0].devotionUsed
        && Boolean(servant)
        && getDevotionTargets(this.state, 0).length > 0,
      tutorialPrompt: this.phase === "planning"
        ? "演出卓から1枚を選ぶか、演出力を次の時間へ残す"
        : this.phase === "result" ? "夜明け" : "二人の手つきを処理しています",
      targetPrompt: this.getTargetPrompt(),
      error: this.error,
      canSubmit: this.phase === "planning" && actionSelected && actionValidation.ok,
      canPass: this.phase === "planning" && passValidation.ok,
      resultKey: null,
      connected: [true, true],
      submitted: [false, false],
    };
  }

  private createState(): GameState {
    return createOpenSupplyGame({
      stageId: this.options.stageId,
      seed: this.options.seed,
      playerNames: ["あなたのご主人", "AIのご主人"],
      servantIds: [this.options.playerServantId, this.options.opponentServantId],
      openingObjectCardId: this.options.openingObjectCardId,
    });
  }

  private createDraft(): PlayerPlan {
    return {
      focusLane: this.state.players[0].focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private passPlan(): PlayerPlan {
    return {
      focusLane: this.draft.focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private isSupplyCardSelectable(cardId: string): boolean {
    const supply = this.state.openSupply;
    const entry = supply?.cards.find((item) => item.cardId === cardId);
    const card = getCard(cardId);
    if (!supply || !entry || !card || !entry.availableTo[0]) return false;
    if (supply.energy[0] < entry.cost) return false;
    return getCardTargets(this.state, card.id).length > 0;
  }

  private getTargetPrompt(): string {
    if (this.error) return this.error;
    if (this.phase === "resolving") return "二つの計画を、順番に夢へ反映しています。";
    if (this.phase === "result") return "夜明けを迎えました。";

    const card = this.selectedCard;
    const devotion = this.selectedDevotion;
    if (devotion) {
      if (devotion.targetType === "none") return "献身《" + devotion.name + "》を捧げます。";
      if (devotion.targetType === "player") {
        const targetIndex = this.draft.target.playerIndex;
        if (targetIndex !== 0 && targetIndex !== 1) {
          return "《満たす一杯》を注ぐご主人を選んでください。";
        }
        const target = this.state.players[targetIndex];
        const after = target.happiness + Math.sign(RULES.idealHappiness - target.happiness) * 3;
        return target.name + "へ注ぎ、幸福度を " + target.happiness + " → " + after
          + " にします。次の時間は注目が固定されます。";
      }
      if (devotion.targetType === "replay") {
        const replayCard = getReplayableCard(this.state, 0);
        if (!replayCard) return "直前の時間に再演できるカードがありません。";
        return this.getCardTargetPrompt(replayCard, 0, true);
      }
      const object = this.state.board.find((item) => item.id === this.draft.target.objectId);
      return object
        ? object.name + "へ献身《" + devotion.name + "》を捧げます。"
        : "献身を向ける夢オブジェクトを選んでください。";
    }
    if (!card) return "演出卓の札か献身を選んでください。";
    const cost = this.state.openSupply?.cards.find((entry) => entry.cardId === card.id)?.cost ?? 0;
    return this.getCardTargetPrompt(card, cost, false);
  }

  private getCardTargetPrompt(card: CardDefinition, cost: number, replay: boolean): string {
    const lead = replay ? "再演する" : "演出力" + cost + "で";
    if (card.kind === "object") {
      if (replay) {
        return this.draft.target.lane
          ? "《" + card.name + "》をレーン" + this.draft.target.lane + "へ再演します。"
          : "再演する《" + card.name + "》を置くレーンを選んでください。";
      }
      return this.draft.target.lane
        ? lead + "《" + card.name + "》をレーン" + this.draft.target.lane + "の深度3へ置きます。"
        : "置きたいレーンの盤面をクリックしてください。";
    }
    if (card.targetType === "none") {
      return lead + "《" + card.name + "》を盤面全体へ使います。";
    }
    if (card.targetType === "lane") {
      return this.draft.target.lane
        ? lead + "《" + card.name + "》をレーン" + this.draft.target.lane + "へ使います。"
        : "対象にするレーンの盤面をクリックしてください。";
    }
    if (card.targetType === "depth") {
      return this.draft.target.depth
        ? lead + "《" + card.name + "》を深度" + this.draft.target.depth + "へ使います。"
        : "対象にする深度の盤面をクリックしてください。";
    }
    const object = this.state.board.find((item) => item.id === this.draft.target.objectId);
    if (!object) return "対象の夢オブジェクトをクリックしてください。";
    if (card.targetType === "object-direction" && !this.draft.target.direction) {
      return object.name + "を選択中。左右どちらかの隣のマスをクリックしてください。";
    }
    if (card.targetType === "object-direction") {
      return object.name + "を" + (this.draft.target.direction === -1 ? "左" : "右") + "へ動かします。";
    }
    if (card.targetType === "object-depth" && !this.draft.target.depth) {
      return object.name + "を選択中。同じレーンの上下どちらかのマスをクリックしてください。";
    }
    if (card.targetType === "object-depth") {
      return object.name + "を深度" + this.draft.target.depth + "へ複製します。";
    }
    return object.name + "へ" + (replay ? "再演する" : "") + "《" + card.name + "》を使います。";
  }
}

function clonePlan(plan: PlayerPlan): PlayerPlan {
  const cloned: PlayerPlan = {
    focusLane: plan.focusLane,
    cardInstanceId: plan.cardInstanceId,
    devotionId: plan.devotionId,
    target: { ...plan.target },
  };
  if (plan.supplyCardId) cloned.supplyCardId = plan.supplyCardId;
  return cloned;
}
